import { useQueries } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import type { ProposalListItem } from '@/lib/proposals/core/types';
import type { TimeFormat } from '@/lib/time';
import {
  calendarDateIn,
  isHour12,
  timeParts,
  useOrgTimeFormat,
} from '@/lib/time';
import type { CalendarEvent } from '@/pages/calendar/utils/eventMapper';
import {
  createMeetingNotesQueryConfig,
  isSummaryEligibleMeeting,
  meetingNeedsSummary,
} from '@/pages/dashboard/components/utils/meetingResearchQuery';

import { useRecapSessionState } from './useRecapSessionState';

export interface UseRecapModeInput {
  calendarEvents: CalendarEvent[];
  actions: ProposalListItem[];
  orgId: number;
  /**
   * IANA timezone every day/hour decision below is made in — the user's
   * profile timezone, which the backend always resolves. Required: there is
   * no browser-local fallback.
   */
  timeZone: string;
  /** Disable recap mode entirely (e.g. during onboarding) */
  disabled?: boolean;
}

export interface UseRecapModeResult {
  // Computed trigger state — all conditions met for CTA to appear
  isTriggered: boolean;
  // Day-of-week detection (in user timezone). 5 = Friday.
  isFriday: boolean;
  // CTA visibility — gated by triggered + not-yet-dismissed
  shouldShowDailyCTA: boolean;
  shouldShowWeeklyCTA: boolean;
  // Active recap modes (after click). Weekly takes precedence over daily.
  isDailyRecapActive: boolean;
  isWeeklyRecapActive: boolean;
  // Activation/deactivation handlers
  activateDailyRecap: () => void;
  activateWeeklyRecap: () => void;
  deactivateRecap: () => void;
  dismissDailyCTA: () => void;
  dismissWeeklyCTA: () => void;
  // Derived data
  todayActions: ProposalListItem[];
  todayActionCount: number;
  weeklyActionsCreated: number;
  weeklyActionsApproved: number;
  meetingsNeedingNotes: CalendarEvent[];
  meetingsNeedingNotesCount: number;
}

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

function getDayOfWeekInTimeZone(date: Date, timeZone: string): number {
  const weekday = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    timeZone,
  }).format(date);
  return WEEKDAY_INDEX[weekday] ?? 0;
}

/**
 * Convert an Intl-formatted hour/dayPeriod pair to an unambiguous 24-hour
 * (0-23) value. Needed because the noon-guard trigger logic below must
 * compare against a real wall-clock hour regardless of the org's 12h/24h
 * display preference (unlike a *displayed* time, "12" alone is ambiguous
 * between noon and midnight when the org uses 12-hour format).
 */
function to24Hour(hour: number, dayPeriod: string | undefined): number {
  if (dayPeriod === 'AM') {
    return hour === 12 ? 0 : hour;
  }
  if (dayPeriod === 'PM') {
    return hour === 12 ? 12 : hour + 12;
  }
  return hour % 24;
}

function getHourInTimeZone(
  date: Date,
  timeZone: string,
  format: TimeFormat
): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    ...timeParts(format),
    timeZone,
  }).formatToParts(date);
  const hourPart = Number(parts.find(part => part.type === 'hour')?.value ?? 0);
  if (!isHour12(format)) {
    return hourPart % 24;
  }
  return to24Hour(
    hourPart,
    parts.find(part => part.type === 'dayPeriod')?.value
  );
}

const HIDE_AFTER_END_MINUTES = 15;
const NOON_HOUR = 12;
const FRIDAY = 5;
const EMPTY_MEETINGS: CalendarEvent[] = [];
// Cap candidates at 20 to keep useQueries bounded (each candidate fires a network request).
const MAX_CANDIDATES = 20;

export function useRecapMode(input: UseRecapModeInput): UseRecapModeResult {
  const { calendarEvents, actions, orgId, timeZone, disabled = false } = input;
  const { timeFormat } = useOrgTimeFormat();

  // `now` updates every 60 s so noon-guard, Friday, and date-filter stay correct
  // in long-lived dashboard sessions without needing a page reload.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => {
      setNow(new Date());
    }, 60_000);
    return () => clearInterval(id);
  }, []);

  const isFriday = useMemo(
    () => getDayOfWeekInTimeZone(now, timeZone) === FRIDAY,
    [now, timeZone]
  );

  const currentHour = useMemo(
    () => getHourInTimeZone(now, timeZone, timeFormat),
    [now, timeZone, timeFormat]
  );

  const todayStr = useMemo(
    () => calendarDateIn(now, timeZone),
    [now, timeZone]
  );

  // Today's timed (non-all-day) meetings.
  const todayTimedMeetings = useMemo(
    () =>
      calendarEvents.filter(
        m =>
          !m.is_all_day &&
          calendarDateIn(new Date(m.start_time), timeZone) === todayStr
      ),
    [calendarEvents, timeZone, todayStr]
  );

  // All timed meetings today must have ended 15+ min ago; false when there are none.
  const allTimedMeetingsEnded = useMemo(() => {
    if (todayTimedMeetings.length === 0) {
      return false;
    }
    const nowMs = now.getTime();
    return todayTimedMeetings.every(
      m =>
        new Date(m.end_time).getTime() + HIDE_AFTER_END_MINUTES * 60_000 < nowMs
    );
  }, [todayTimedMeetings, now]);

  // Noon guard prevents premature triggers (e.g., 9am standup user shouldn't see recap at 9:15am).
  const isTriggered =
    !disabled && allTimedMeetingsEnded && currentHour >= NOON_HOUR;

  // Session-only activation/dismissal state — intentionally not persisted across page loads.
  const {
    dailyActivated,
    dailyDismissed,
    weeklyActivated,
    weeklyDismissed,
    activateDailyRecap,
    activateWeeklyRecap,
    deactivateRecap,
    dismissDailyCTA,
    dismissWeeklyCTA,
  } = useRecapSessionState(todayStr);

  const shouldShowWeeklyCTA =
    isTriggered && isFriday && !weeklyDismissed && !weeklyActivated;

  // Candidates for meetings needing notes: today's past timed meetings, ended >1hr ago, >1 guest.
  // Cap at MAX_CANDIDATES to keep useQueries bounded.
  const candidates = useMemo(() => {
    const nowMs = now.getTime();
    return calendarEvents
      .filter(
        m =>
          calendarDateIn(new Date(m.start_time), timeZone) === todayStr &&
          isSummaryEligibleMeeting({
            endTime: m.end_time,
            isAllDay: m.is_all_day,
            guestCount: m.guests?.length ?? 0,
            referenceMs: nowMs,
          })
      )
      .slice(0, MAX_CANDIDATES);
  }, [calendarEvents, timeZone, todayStr, now]);

  // Captured meeting notes (Granola/Zoom/Meet/manual) are what "has a summary"
  // means here. Research prep briefs are written before the meeting and are
  // deliberately not consulted — see meetingNeedsSummary.
  const queries = useMemo(
    () =>
      candidates.map(m => ({
        ...createMeetingNotesQueryConfig(orgId, m.id),
        enabled: !disabled && candidates.length > 0,
      })),
    [candidates, disabled, orgId]
  );

  const notesResults = useQueries({ queries });

  // A meeting needs a summary when its notes query resolved empty. Skip
  // meetings whose query is still loading or errored (avoid false positives).
  const meetingsNeedingNotes = useMemo(() => {
    if (candidates.length === 0) {
      return EMPTY_MEETINGS;
    }
    return candidates.filter((_, i) => {
      const notes = notesResults[i];
      if (!notes || notes.isLoading || notes.error) {
        return false;
      }
      return meetingNeedsSummary(notes.data);
    });
  }, [candidates, notesResults]);

  // Today's actions filtered by created_at date in user timezone.
  const todayActions = useMemo(
    () =>
      actions.filter(
        a => calendarDateIn(new Date(a.created_at), timeZone) === todayStr
      ),
    [actions, timeZone, todayStr]
  );

  // Weekly (Mon–Fri) action stats. Shares a single weekDates Set to avoid double computation.
  // Weekly action counts limited by incoming actions array — if the caller only passes
  // pending proposals, weeklyActionsApproved will be 0.
  const { weeklyActionsCreated, weeklyActionsApproved } = useMemo(() => {
    const dayOfWeek = getDayOfWeekInTimeZone(now, timeZone);
    // On Sunday (0): daysToMonday=6 → Mon=today-6, Fri=today-2 (past week).
    // On Saturday (6): daysToMonday=5 → Mon=today-5, Fri=today-1 (week just ended).
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const weekDates = new Set<string>();
    for (let i = 0; i < 5; i++) {
      const offsetDays = i - daysToMonday;
      const d = new Date(now.getTime() + offsetDays * 24 * 60 * 60 * 1000);
      weekDates.add(calendarDateIn(d, timeZone));
    }
    const created = actions.filter(a =>
      weekDates.has(calendarDateIn(new Date(a.created_at), timeZone))
    ).length;
    const approved = actions.filter(
      a =>
        a.resolved_at !== null &&
        a.resolution === 'approved' &&
        weekDates.has(calendarDateIn(new Date(a.resolved_at), timeZone))
    ).length;
    return { weeklyActionsCreated: created, weeklyActionsApproved: approved };
  }, [actions, timeZone, now]);

  // Only show daily CTA when there's something to recap.
  const shouldShowDailyCTA =
    isTriggered &&
    !dailyDismissed &&
    !dailyActivated &&
    (todayActions.length > 0 || meetingsNeedingNotes.length > 0);

  // Weekly takes precedence over daily per the feature spec.
  // Gate by `disabled` so that flipping `disabled` true (e.g., onboarding starts
  // mid-session) suppresses any already-activated recap mode, matching the
  // documented "Disable recap mode entirely" contract on the `disabled` prop.
  const isWeeklyRecapActive = !disabled && weeklyActivated;
  const isDailyRecapActive = !disabled && dailyActivated && !weeklyActivated;

  return useMemo(
    () => ({
      isTriggered,
      isFriday,
      shouldShowDailyCTA,
      shouldShowWeeklyCTA,
      isDailyRecapActive,
      isWeeklyRecapActive,
      activateDailyRecap,
      activateWeeklyRecap,
      deactivateRecap,
      dismissDailyCTA,
      dismissWeeklyCTA,
      todayActions,
      todayActionCount: todayActions.length,
      weeklyActionsCreated,
      weeklyActionsApproved,
      meetingsNeedingNotes,
      meetingsNeedingNotesCount: meetingsNeedingNotes.length,
    }),
    [
      isTriggered,
      isFriday,
      shouldShowDailyCTA,
      shouldShowWeeklyCTA,
      isDailyRecapActive,
      isWeeklyRecapActive,
      activateDailyRecap,
      activateWeeklyRecap,
      deactivateRecap,
      dismissDailyCTA,
      dismissWeeklyCTA,
      todayActions,
      weeklyActionsCreated,
      weeklyActionsApproved,
      meetingsNeedingNotes,
    ]
  );
}
